/**
 * Stripe Elements integration for the booking widget.
 *
 * Dynamically loads Stripe.js, mounts a CardElement, and provides
 * createPaymentMethod() for tokenizing card details.
 */

declare global {
  interface Window {
    Stripe?: (key: string, opts?: { stripeAccount?: string }) => StripeInstance;
  }
}

interface StripeInstance {
  elements: (opts?: Record<string, unknown>) => StripeElements;
  createPaymentMethod: (opts: Record<string, unknown>) => Promise<{ paymentMethod?: { id: string }; error?: { message: string } }>;
}

interface StripeElements {
  create: (type: string, opts?: Record<string, unknown>) => StripeCardElement;
  submit: () => Promise<{ error?: { message: string } }>;
  update: (opts: Record<string, unknown>) => void;
}

interface StripeCardElement {
  mount: (el: string | HTMLElement) => void;
  unmount: () => void;
  destroy: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on: (event: string, handler: (e: any) => void) => void;
}

let stripePromise: Promise<void> | null = null;
let stripeInstance: StripeInstance | null = null;
let cardElement: StripeCardElement | null = null;
let cardComplete = false;
// Has the card iframe finished loading? (The deposit screen waits for it.)
let cardReady = false;
// Notified whenever the card element's completeness changes, so the caller can
// re-evaluate the pay button (typing happens inside Stripe's iframe and never
// fires the widget's own input/click handlers).
let cardChangeCallback: (() => void) | null = null;

/** Open the connections the payment step will need BEFORE it is reached: the
 *  wallet button spends most of its seconds on DNS + TLS to Stripe's and
 *  Google's hosts. Hints only — the browser may ignore them, nothing breaks. */
let _hinted = false;
function addConnectionHints(): void {
  if (_hinted || typeof document === 'undefined') return;
  _hinted = true;
  for (const href of ['https://js.stripe.com', 'https://m.stripe.network', 'https://api.stripe.com', 'https://pay.google.com']) {
    const l = document.createElement('link');
    l.rel = 'preconnect'; l.href = href; l.crossOrigin = 'anonymous';
    document.head.appendChild(l);
  }
}

/**
 * Load Stripe.js script if not already loaded.
 */
function loadStripeJs(): Promise<void> {
  addConnectionHints();
  if (stripePromise) return stripePromise;
  if (window.Stripe) {
    stripePromise = Promise.resolve();
    return stripePromise;
  }
  stripePromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Allow a later retry to re-attempt the script load (ad blockers are
      // sometimes toggled off after the user sees the card-entry error).
      stripePromise = null;
      script.remove();
      reject(new Error('Failed to load Stripe.js'));
    };
    document.head.appendChild(script);
  });
  return stripePromise;
}

/**
 * Initialize Stripe with the publishable key.
 * Call once after receiving widget-config.
 */
let lastPublishableKey = '';
let lastStripeAccountId: string | undefined;

export async function initStripe(publishableKey: string, stripeAccountId?: string): Promise<void> {
  if (!publishableKey) return;
  lastPublishableKey = publishableKey;
  lastStripeAccountId = stripeAccountId;
  try {
    await loadStripeJs();
    if (window.Stripe) {
      // Connect: platform publishable key + connected account → tokenization
      // happens on the business's own Stripe account.
      stripeInstance = stripeAccountId
        ? window.Stripe(publishableKey, { stripeAccount: stripeAccountId })
        : window.Stripe(publishableKey);
    }
  } catch (e) {
    // Blocked script (ad/privacy blockers) or sandboxed context — don't
    // surface an unhandled rejection at boot; the payment step retries and
    // shows a visible error if Stripe still can't load.
    console.warn('[widget] Stripe unavailable:', e);
  }
}

/**
 * Mount a Stripe CardElement into the given container.
 * The container should have id="pp-stripe-card-element".
 *
 * If Stripe failed to initialize at boot (ad/privacy blocker, sandboxed
 * embed), retry once here — the payment step is the moment that matters —
 * and if it still can't load, show a visible explanation instead of a
 * silent empty card box.
 */
export function mountCardElement(containerId: string, accentColor?: string, onChange?: () => void): void {
  cardChangeCallback = onChange || null;
  if (!stripeInstance) {
    if (!lastPublishableKey) return;
    initStripe(lastPublishableKey, lastStripeAccountId).then(() => {
      if (stripeInstance) {
        _mountNow(containerId, accentColor);
      } else {
        _showCardLoadError(containerId);
      }
    });
    return;
  }
  _mountNow(containerId, accentColor);
}

function _showCardLoadError(containerId: string): void {
  requestAnimationFrame(() => {
    const el = document.getElementById(containerId);
    if (!el) return;
    el.innerHTML = '';
    const msg = document.createElement('div');
    msg.style.cssText = 'padding:10px 12px;border:1px solid #f0c8c8;border-radius:8px;background:#fdf3f3;color:#b04a4a;font-size:13px;line-height:1.45;';
    msg.textContent = "Secure card entry couldn't load. An ad or privacy blocker may be preventing it — please disable it for this page and try again, or contact us to complete your booking.";
    el.appendChild(msg);
  });
}

/** What state the card field was in — recorded with a payment error, because
 *  "We could not retrieve data from the specified Element" says only THAT the
 *  field was gone, never why (2026-09-19: it still happened once after the
 *  redraw fix — most likely an old cached bundle, which is why the build time is
 *  reported). No card data. */
let _mountCount = 0;
let _lastContainerId = '';
export function cardDiagnostics(): Record<string, unknown> {
  const boxes = _lastContainerId && typeof document !== 'undefined'
    ? document.querySelectorAll(`[id="${_lastContainerId}"]`) : ([] as unknown as NodeListOf<Element>);
  return {
    built: process.env.WIDGET_BUILT_AT || '',                   // which bundle this is
    ready: cardReady, complete: cardComplete, hasElement: !!cardElement, mounts: _mountCount,
    boxes: boxes.length,                                        // 2 = two widgets on the page
    iframes: Array.from(boxes).map(b => b.querySelectorAll('iframe').length),
    wallet: !!pendingWalletPm,
    widgets: typeof document !== 'undefined' ? document.querySelectorAll('[id="booking-widget-panel"]').length : 0,
    scripts: typeof document !== 'undefined' ? document.querySelectorAll('script[src*="widget.js"], script[src*="portal.js"]').length : 0,
  };
}

/** Stripe's own words for "the card field was not there when I looked". */
export function isLostCardFieldError(message: string): boolean {
  return /could not retrieve data from the specified Element|Element you are attempting to use/i.test(message || '');
}

function _mountNow(containerId: string, _accentColor?: string): void {
  if (!stripeInstance) return;
  _mountCount += 1;
  _lastContainerId = containerId;
  // Destroy previous if exists
  if (cardElement) {
    try { cardElement.destroy(); } catch (_) { /* ignore */ }
    cardElement = null;
  }
  cardComplete = false;
  cardReady = false;

  const elements = stripeInstance.elements();
  cardElement = elements.create('card', {
    style: {
      base: {
        // 16px: iOS Safari zooms the page into any focused field smaller than
        // that, and a zoomed page under a fixed panel puts every tap off target.
        fontSize: '16px',
        color: '#333',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        '::placeholder': { color: '#999' },
      },
      invalid: { color: '#e53e3e' },
    },
  });

  // Wait a tick for DOM to render
  requestAnimationFrame(() => {
    const el = document.getElementById(containerId);
    if (el && cardElement) {
      cardElement.on('ready', () => { cardReady = true; });
      cardElement.mount(el);
      cardElement.on('change', (event) => {
        const next = !!event.complete;
        const changed = next !== cardComplete;
        cardComplete = next;
        // Re-evaluate the pay button when completeness flips (enable once the
        // card is valid, disable again if edited back to invalid).
        if (changed && cardChangeCallback) cardChangeCallback();
      });
    }
  });
}

/**
 * Returns true if the Stripe card element has complete, valid input.
 */
export function isCardComplete(): boolean {
  return cardComplete || !!pendingWalletPm || walletPrecheck;
}

/**
 * Returns true if Stripe is initialized (publishable key was provided).
 */
export function isStripeEnabled(): boolean {
  return !!stripeInstance;
}

/**
 * Create a Stripe PaymentMethod from the mounted CardElement.
 * Returns the payment method ID string.
 * Throws on error.
 */
export async function createPaymentMethod(billingDetails?: {
  name?: string;
  email?: string;
}): Promise<string> {
  // Apple Pay / Google Pay already produced the payment method: the purchase
  // runs through exactly the same code as a typed card, it just skips the
  // card form. Consumed once.
  if (pendingWalletPm) {
    const id = pendingWalletPm;
    pendingWalletPm = null;
    return id;
  }
  if (!stripeInstance || !cardElement) {
    throw new Error('Stripe is not initialized or card element is not mounted');
  }
  const result = await stripeInstance.createPaymentMethod({
    type: 'card',
    card: cardElement,
    billing_details: billingDetails,
  });
  if (result.error) {
    throw new Error(result.error.message || 'Card validation failed');
  }
  if (!result.paymentMethod) {
    throw new Error('No payment method returned');
  }
  return result.paymentMethod.id;
}

/**
 * Unmount and clean up the card element.
 */
export function destroyCardElement(): void {
  if (cardElement) {
    try { cardElement.destroy(); } catch (_) { /* ignore */ }
    cardElement = null;
    cardComplete = false;
  }
  cardChangeCallback = null;
}

// ── Apple Pay / Google Pay (Stripe Express Checkout Element) ───────────────
//
// The wallet sheet hands back an ordinary card PaymentMethod, which the server
// charges exactly like a typed card (same endpoint, same saved-card rules).
// Only card wallets are offered — Link / PayPal / Klarna are switched off.
// Nothing shows unless the device actually has a wallet ready, and Apple Pay
// additionally needs the page's domain registered on the spa's Stripe account.

let walletElement: StripeCardElement | null = null;
let walletElements: StripeElements | null = null;
let pendingWalletPm: string | null = null;
// Has the wallet button reported in (shown, nothing to show, or failed)?
let walletSettled = true;
// True only while the widget asks "would this be payable if the card were
// done?" before opening the wallet sheet.
let walletPrecheck = false;

export interface WalletHooks {
  /** Before the sheet opens: return false to keep it closed (the widget shows what's missing). */
  canStart: () => boolean;
  /** The wallet produced a payment method: run the normal pay action. */
  onPaid: () => void;
}

/** Evaluate `fn` as if the card form were complete. */
export function withCardAssumedComplete<T>(fn: () => T): T {
  walletPrecheck = true;
  try { return fn(); } finally { walletPrecheck = false; }
}

export function mountWalletButton(containerId: string, amount: number, hooks: WalletHooks, currency = 'usd'): void {
  destroyWalletButton();
  walletSettled = false;
  const cents = Math.round((Number(amount) || 0) * 100);
  if (!stripeInstance || cents <= 0) { walletSettled = true; return; }
  const stripe = stripeInstance;
  try {
    walletElements = stripe.elements({
      mode: 'payment', amount: cents, currency: currency.toLowerCase(),
      paymentMethodCreation: 'manual', paymentMethodTypes: ['card'],
    });
    walletElement = walletElements.create('expressCheckout', {
      paymentMethods: { applePay: 'auto', googlePay: 'auto', link: 'never', paypal: 'never', amazonPay: 'never' },
      buttonHeight: 44,
    });
  } catch (e) {
    console.warn('[widget] wallet button unavailable:', e);
    walletElement = null;
    walletSettled = true;
    return;
  }
  const el = walletElement;
  const els = walletElements;
  requestAnimationFrame(() => {
    const host = document.getElementById(containerId);
    if (el !== walletElement) return;
    if (!host) { walletSettled = true; return; }
    const slot = host.querySelector<HTMLElement>('[data-wallet-slot]') || host;
    el.on('ready', (ev) => {
      const avail = ev?.availablePaymentMethods;
      const any = !!avail && Object.values(avail).some(Boolean);
      host.classList.toggle('pp-wallet--pending', !any);
      walletSettled = true;
    });
    // No wallet for this account/page: stay hidden, the card form is right below.
    el.on('loaderror', () => { host.classList.add('pp-wallet--pending'); walletSettled = true; });
    el.on('click', (ev) => {
      if (!hooks.canStart()) return;       // sheet stays closed
      ev.resolve();
    });
    el.on('confirm', async (ev) => {
      const sub = await els!.submit();
      if (sub.error) { ev.paymentFailed?.({ reason: 'fail' }); return; }
      const res = await stripe.createPaymentMethod({ elements: els });
      if (res.error || !res.paymentMethod) { ev.paymentFailed?.({ reason: 'fail' }); return; }
      pendingWalletPm = res.paymentMethod.id;
      hooks.onPaid();
    });
    // Listeners first: an event nobody was listening for yet is "Unhandled".
    el.mount(slot);
  });
}

/** WARM-UP: the first Apple Pay / Google Pay button on a page is slow — Stripe
 *  pulls its wallet frame and Google's pay.js, then asks the device whether a
 *  wallet is ready (several seconds on a phone). Mount a throwaway button off
 *  screen one step EARLY (the contact / details step), so by the time the
 *  payment step mounts the real one those downloads are in the browser cache.
 *  Once per page; never shown, never clickable, removed when it reports in. */
let _warmed = false;
export function prewarmWallet(): void {
  if (_warmed || !stripeInstance || typeof document === 'undefined') return;
  _warmed = true;
  try {
    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = 'position:fixed;left:-9999px;top:0;width:320px;height:48px;overflow:hidden;visibility:hidden;pointer-events:none;';
    document.body.appendChild(host);
    const els = stripeInstance.elements({
      mode: 'payment', amount: 100, currency: 'usd',
      paymentMethodCreation: 'manual', paymentMethodTypes: ['card'],
    });
    const el = els.create('expressCheckout', {
      paymentMethods: { applePay: 'auto', googlePay: 'auto', link: 'never', paypal: 'never', amazonPay: 'never' },
    });
    let done = false;
    const cleanup = () => {
      if (done) return;
      done = true;
      try { el.destroy(); } catch (_) { /* ignore */ }
      host.remove();
    };
    el.on('ready', cleanup);
    el.on('loaderror', cleanup);
    setTimeout(cleanup, 12000);
    el.mount(host);
  } catch (e) {
    console.warn('[widget] wallet warm-up skipped:', e);
  }
}

/** Keep the sheet's total in step with the page (tip, promotion…). */
export function updateWalletAmount(amount: number): void {
  const cents = Math.round((Number(amount) || 0) * 100);
  if (walletElements && cents > 0) {
    try { walletElements.update({ amount: cents }); } catch (_) { /* ignore */ }
  }
}

export function destroyWalletButton(): void {
  if (walletElement) {
    try { walletElement.destroy(); } catch (_) { /* ignore */ }
  }
  walletElement = null;
  walletElements = null;
}

/** Drop an unused wallet payment method (the purchase failed before charging). */
export function clearPendingWallet(): void {
  pendingWalletPm = null;
}

/**
 * Resolves once the card form has loaded and the wallet button has decided
 * whether to show — so a payment screen can appear complete instead of the
 * Apple Pay / Google Pay button popping in a second later. Never waits longer
 * than `maxMs`: a slow wallet must not hold up paying by card.
 */
export function paymentUiReady(maxMs = 3000): Promise<void> {
  const started = Date.now();
  return new Promise(resolve => {
    const check = () => {
      if ((cardReady || !cardElement) && walletSettled) return resolve();
      if (Date.now() - started >= maxMs) return resolve();
      setTimeout(check, 50);
    };
    check();
  });
}
